"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Building2 } from "lucide-react";
import { SaveButtonSpinner } from "@/components/save-button-spinner";
import { Skeleton } from "@/components/ui/skeleton";
import { SmoothHeight } from "@/components/loading-skeletons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { getUserName } from "@/utils/utils";
import { deleteCookie } from "@/lib/cookies";
import { login, logout } from "@/app/actions";
import { getUploadUrl } from "@/services/upload.client";
import { showApiErrorToast } from "@/lib/api-error-toast";
import {
  useCurrentUser,
  useCurrentOrganization,
  usePortalReducers,
} from "@/hooks/spacetime";

interface InviteAcceptDialogProps {
  controlledOpen?: boolean;
  invitation: {
    orgId: string;
    userId: string; // historically the invited person's email
    token: string;
  };
  onClose: () => void;
}

interface InviteOrganizationDetails {
  name: string;
  logo: string;
}

interface InviteInviterDetails {
  name: string;
  email: string;
}

export default function InviteAcceptDialog({
  controlledOpen = true,
  invitation,
  onClose,
}: InviteAcceptDialogProps) {
  const { user } = useCurrentUser();
  const { selectOrganization } = useCurrentOrganization();
  const { inviteDetails, acceptInvite, declineInvite } = usePortalReducers();

  const [organizationDetails, setOrganizationDetails] =
    useState<InviteOrganizationDetails | null>(null);
  const [inviterDetails, setInviterDetails] =
    useState<InviteInviterDetails | null>(null);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsInvalid(false);

    (async () => {
      try {
        const details = await inviteDetails({
          organizationId: invitation.orgId,
          inviteToken: invitation.token,
        });

        if (!details) {
          if (cancelled) return;
          deleteCookie("inviteParams", "/");
          setIsInvalid(true);
          setIsLoading(false);
          return;
        }

        let organizationLogo = details.organizationLogo;
        if (organizationLogo && organizationLogo.includes("organizations/")) {
          // Legacy invite-param presign path (src/app/api/upload/route.ts's
          // invite-detail branch) — presign is best-effort and 401s for
          // unauthenticated callers (invite token no longer grants presign
          // access). On failure, render the raw logo string; the Avatar
          // component will not load it and fall back to the Building2 icon.
          try {
            organizationLogo = await getUploadUrl(organizationLogo, {
              orgId: invitation.orgId,
              userId: invitation.userId,
              token: invitation.token,
            });
          } catch (presignError: any) {
            console.warn(
              "Logo presign failed, using raw logo:",
              presignError.message,
            );
            // Keep the raw organizationLogo; Avatar will use the fallback icon
          }
        }

        if (cancelled) return;
        setOrganizationDetails({
          name: details.organizationName,
          logo: organizationLogo,
        });
        setInviterDetails({
          name: details.inviterName,
          email: details.inviterEmail,
        });
        setInvitedEmail(details.invitedEmail);
        setIsLoading(false);
      } catch (error: any) {
        if (cancelled) return;
        console.warn(error.message);
        setIsInvalid(true);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [invitation.orgId, invitation.token, invitation.userId, inviteDetails]);

  const normalize = (email: string) => email.trim().toLowerCase();
  const isUserMismatch =
    !isLoading &&
    !isInvalid &&
    !!user &&
    !!invitedEmail &&
    normalize(user.email) !== normalize(invitedEmail);
  const requiresSignIn = !isLoading && !isInvalid && !isUserMismatch && !user;

  const handleAccept = useCallback(async () => {
    setIsNavigating(true);

    try {
      await acceptInvite({
        organizationId: invitation.orgId,
        inviteToken: invitation.token,
      });
      // Only clear the cookie once accept has actually succeeded — if it
      // rejects (transient error) and the user reloads instead of retrying,
      // the still-valid invitation must still be recoverable in that
      // session.
      deleteCookie("inviteParams", "/");
      selectOrganization(invitation.orgId);
      onClose();
    } catch (error: any) {
      console.warn("Failed to accept invitation", error.message);
      showApiErrorToast("Failed to accept invitation", error);
      setIsNavigating(false);
    }
  }, [
    acceptInvite,
    invitation.orgId,
    invitation.token,
    onClose,
    selectOrganization,
  ]);

  const handleDecline = useCallback(async () => {
    setIsNavigating(true);
    deleteCookie("inviteParams", "/");

    try {
      await declineInvite({
        organizationId: invitation.orgId,
        inviteToken: invitation.token,
      });
    } catch (error: any) {
      console.warn("Failed to decline invitation", error.message);
      showApiErrorToast("Failed to decline invitation", error);
    } finally {
      onClose();
    }
  }, [declineInvite, invitation.orgId, invitation.token, onClose]);

  const handleDismiss = useCallback(() => {
    deleteCookie("inviteParams", "/");
    onClose();
  }, [onClose]);

  const handleSignOut = useCallback(async () => {
    setIsNavigating(true);
    deleteCookie("inviteParams", "/");

    try {
      await logout();
    } catch (error) {
      showApiErrorToast("Auth redirect failed", error);
      setIsNavigating(false);
    }
  }, []);

  const handleSignIn = useCallback(async () => {
    setIsNavigating(true);

    try {
      await login();
    } catch (error) {
      showApiErrorToast("Sign-in redirect failed", error);
      setIsNavigating(false);
    }
  }, []);

  const Message = () => (
    <p>
      You&apos;ve been invited by{" "}
      <b className="text-gray-1100 dark:text-gray-100">
        {getUserName(
          inviterDetails?.name || "UNASSIGNED",
          inviterDetails?.email,
        )}
      </b>{" "}
      (
      <Link
        href={`mailto:${inviterDetails?.email}`}
        className="text-brand-accent hover:underline"
      >
        {inviterDetails?.email}
      </Link>
      ) to join the{" "}
      <b className="text-gray-1100 dark:text-gray-100">
        {organizationDetails?.name}
      </b>{" "}
      team on Libum Portal.
    </p>
  );

  const OrganizationHeading = () => (
    <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
      <Avatar className="mx-auto mb-4 size-12 rounded-lg">
        <AvatarImage src={organizationDetails?.logo} alt="" />
        <AvatarFallback className="rounded-lg">
          <Building2 className="size-5 text-muted-foreground" />
        </AvatarFallback>
      </Avatar>
      Join the <b>{organizationDetails?.name}</b> team!
    </DialogTitle>
  );

  return (
    <Dialog open={controlledOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-card">
        <DialogHeader className="flex">
          <SmoothHeight className="flex flex-col space-y-1.5 text-left">
            {isLoading ? (
              <>
                <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                  Fetching your invitation..
                </DialogTitle>
                <DialogDescription />
                <div
                  data-testid="invite-skeleton"
                  className="flex flex-col items-center gap-3 py-4"
                >
                  <Skeleton className="size-12 rounded-lg" />
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-9 w-full" />
                </div>
              </>
            ) : isInvalid ? (
              <>
                <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                  Uh oh! Something went wrong
                </DialogTitle>
                <DialogDescription />
                <div className="flex flex-col items-center py-4 text-center">
                  <Image
                    className="mb-4"
                    src="/emojis/monocle-face.svg"
                    alt=""
                    width={24}
                    height={24}
                  />
                  <p>
                    Hmm, this invitation seems to be invalid. Please{" "}
                    <b>close this tab</b> & ask for a <b>new invitation</b>.
                  </p>
                </div>
              </>
            ) : isUserMismatch ? (
              <>
                <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                  Freeze! Not so fast...
                </DialogTitle>
                <DialogDescription />
                <div className="flex flex-col items-center py-4 text-center">
                  <Image
                    className="mb-4"
                    src="/emojis/cold-face.svg"
                    alt=""
                    width={24}
                    height={24}
                  />
                  <p>
                    This invitation was meant for another user. Please sign in
                    as the intended user and try again.
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <SaveButtonSpinner show={isNavigating} />
                  <Button variant="form" onClick={handleDismiss}>
                    Dismiss
                  </Button>
                  <Button variant="destructive" onClick={handleSignOut}>
                    Sign out
                  </Button>
                </div>
              </>
            ) : requiresSignIn ? (
              <>
                <OrganizationHeading />
                <DialogDescription />
                <div className="flex flex-col py-4 text-center">
                  <Message />
                  <p className="pt-6 text-sm text-muted-foreground">
                    Sign in to accept this invitation.
                  </p>
                  <div className="ml-auto flex items-center gap-2 pt-6">
                    <SaveButtonSpinner show={isNavigating} />
                    <Button variant="form" onClick={handleDecline}>
                      Decline
                    </Button>
                    <Button onClick={handleSignIn}>Sign in</Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <OrganizationHeading />
                <DialogDescription />
                <div className="flex flex-col py-4 text-center">
                  <Message />
                  <div className="ml-auto flex items-center gap-2 pt-8">
                    <SaveButtonSpinner show={isNavigating} />
                    <Button variant="form" onClick={handleDecline}>
                      Decline
                    </Button>
                    <Button onClick={handleAccept}>Accept</Button>
                  </div>
                </div>
              </>
            )}
          </SmoothHeight>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
